import { VISIBILITIES, type Visibility } from "./user";
import { VERIFICATION_STATUSES, type VerificationStatus } from "./source";
import { PLACE_STATUSES, type PlaceStatus } from "./place";
import { EVENT_STATUSES, type EventStatus } from "./event";
import { REPORT_STATUSES, type ReportStatus } from "./report";
import { STAMP_RULE_TYPES, type StampRuleType } from "./stamp";
import { LIVE_TYPES, LIVE_STATUSES, type LiveType, type LiveStatus } from "./live";
import { VERIFICATION_LEVELS, type VerificationLevel } from "./visit";

/**
 * 스키마는 전방 호환을 위해 상태값을 `string` 으로 받는다 —
 * 서버가 새 값을 추가해도 파싱이 깨지면 안 되기 때문이다 (09 §5.2).
 * 화면은 좁은 타입으로 다루므로, 경계에서 한 번만 좁힌다.
 *
 * ⚠️ 규칙: 모르는 값은 **가장 안전한 쪽**으로 떨어뜨린다.
 * 공개 범위를 모르면 비공개로, 검증 상태를 모르면 미검증으로 본다. Fail closed.
 */
function narrow<T extends string>(allowed: readonly T[], value: string | undefined | null, fallback: T): T {
  return value != null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export const toVisibility = (v?: string | null): Visibility => narrow(VISIBILITIES, v, "PRIVATE");
export const toVerificationStatus = (v?: string | null): VerificationStatus => narrow(VERIFICATION_STATUSES, v, "UNVERIFIED");
export const toPlaceStatus = (v?: string | null): PlaceStatus => narrow(PLACE_STATUSES, v, "ACTIVE");
export const toEventStatus = (v?: string | null): EventStatus => narrow(EVENT_STATUSES, v, "SCHEDULED");
export const toReportStatus = (v?: string | null): ReportStatus => narrow(REPORT_STATUSES, v, "DRAFT");
export const toStampRuleType = (v?: string | null): StampRuleType => narrow(STAMP_RULE_TYPES, v, "FIRST_VISIT");
export const toLiveType = (v?: string | null): LiveType => narrow(LIVE_TYPES, v, "NORMAL");
export const toLiveStatus = (v?: string | null): LiveStatus => narrow(LIVE_STATUSES, v, "EXPIRED");
export const toVerificationLevel = (v?: string | null): VerificationLevel => narrow(VERIFICATION_LEVELS, v, "SELF_REPORTED");

/** 서버가 보낸 값이 우리가 아는 값인지 — 새 값 유입을 로깅하고 싶을 때 쓴다. */
export function isKnown<T extends string>(allowed: readonly T[], value: string): boolean {
  return (allowed as readonly string[]).includes(value);
}
